import React from 'react';
import { IAutocompleteConfig } from './autocompleteConfig';

export interface ISearchBoxProps {
    /** Placeholder text. Callers pass their own localization key — the header and page use different ones. */
    placeholder: string;
    style?: React.CSSProperties;
    /** Controlled value, when the caller keeps the text in its own state (the search page does). */
    value?: string;
    onValueChange?: (value: string) => void;
    /** Overrides what Enter / the magnifier does. Defaults to navigating to the search page. */
    onSearch?: (submitted: string) => void;
    /**
     * Whether this storefront has search suggestions at all (the entitlement-gated master switch).
     * Passed in rather than queried inside the component so the plain search box stays free of any
     * store dependency — see the note on the split in SearchBox.
     */
    suggestionsAvailable?: boolean;
    /**
     * THIS bar's autocomplete config, from its own context blob (header / home page / search page).
     * Each bar is enabled, scoped to columns and styled independently.
     */
    autocomplete?: IAutocompleteConfig;
}

/**
 * Controlled/uncontrolled value handling, shared by both search-box variants so they behave
 * identically as you type and differ only in whether a popout appears.
 */
export function useSearchBoxValue(props: ISearchBoxProps) {
    const { onValueChange, value: controlledValue } = props;
    const [internalKeywords, setInternalKeywords] = React.useState('');
    const keywords = controlledValue !== undefined ? controlledValue : internalKeywords;

    const setKeywords = React.useCallback((next: string) => {
        if (onValueChange) onValueChange(next);
        if (controlledValue === undefined) setInternalKeywords(next);
    }, [onValueChange, controlledValue]);

    return { keywords, setKeywords };
}

/** Minimum characters before suggestions are requested. Mirrored (and enforced) by the BFF. */
export const MIN_SUGGEST_CHARS = 3;

/**
 * How long typing must pause before a suggest request goes out.
 *
 * 100ms rather than the original 150: now that the text columns are requested separately and answered
 * from the BFF's cache, the debounce is a visible share of the time to first paint rather than a
 * rounding error next to the hydration it used to wait on. Still long enough that typing straight
 * through a word fires one request, not six.
 */
export const SUGGEST_DEBOUNCE_MS = 100;
